import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  Timestamp,
  writeBatch,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../../../firebase';

/**
 * Notification Service
 * Handles CRUD operations for persistent notifications
 */
class NotificationService {
  collectionName = 'notifications';

  /**
   * Create a single notification
   */
  async createNotification({
    type,
    priority,
    category,
    title,
    message,
    metadata = {},
    schoolId,
    recipientIds = [],
    roleFilter = [],
    createdById,
    actionable = false,
    actionUrl = null,
    actionLabel = null,
    groupKey = null,
    expiresInDays = 30,
  }) {
    if (!schoolId) {
      throw new Error('schoolId is required');
    }

    const icon = this.getIconForType(type, priority);

    const notificationData = {
      type,
      priority,
      category,
      title,
      message,
      icon,
      metadata,
      schoolId,
      recipientIds,
      roleFilter,
      createdById: createdById || null,
      createdAt: serverTimestamp(),
      readBy: [],
      dismissedBy: [],
      expiresAt: expiresInDays
        ? Timestamp.fromDate(new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000))
        : null,
      actionable,
      actionUrl,
      actionLabel,
      groupKey,
      isGrouped: false,
      groupCount: null,
    };

    try {
      const docRef = await addDoc(collection(db, this.collectionName), notificationData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Create multiple notifications in batch
   */
  async createBulkNotifications(notifications) {
    const batch = writeBatch(db);
    const notificationRefs = [];

    notifications.forEach((notificationData) => {
      const docRef = doc(collection(db, this.collectionName));
      batch.set(docRef, {
        ...notificationData,
        icon: this.getIconForType(notificationData.type, notificationData.priority),
        createdAt: serverTimestamp(),
        readBy: [],
        dismissedBy: [],
        isGrouped: false,
        groupCount: null,
      });
      notificationRefs.push(docRef);
    });

    try {
      await batch.commit();
      return notificationRefs.map((ref) => ref.id);
    } catch (error) {
      console.error('Error creating bulk notifications:', error);
      throw error;
    }
  }

  /**
   * Create grouped notification
   * Groups similar notifications (e.g., 5 mentions submitted today)
   */
  async createGroupedNotification(groupKey, items, schoolId) {
    if (!groupKey || !items || items.length === 0) {
      throw new Error('groupKey and items are required');
    }

    try {
      // Check if group already exists
      const existingGroup = await this.getGroupHeader(groupKey, schoolId);

      if (existingGroup) {
        // Update group count
        await updateDoc(doc(db, this.collectionName, existingGroup.id), {
          groupCount: existingGroup.groupCount + items.length,
          metadata: this.mergeGroupMetadata(existingGroup.metadata, items),
        });

        // Mark individual items as grouped
        const batch = writeBatch(db);
        items.forEach((item) => {
          if (item.id) {
            batch.update(doc(db, this.collectionName, item.id), { isGrouped: true });
          }
        });
        await batch.commit();

        return existingGroup.id;
      }

      // Create new group header
      const groupData = {
        type: items[0].type,
        priority: this.calculateGroupPriority(items),
        category: items[0].category,
        title: this.generateGroupTitle(items),
        message: this.generateGroupMessage(items),
        icon: this.getIconForType(items[0].type, items[0].priority),
        metadata: this.mergeGroupMetadata({}, items),
        schoolId,
        recipientIds: items[0].recipientIds || [],
        roleFilter: items[0].roleFilter || [],
        createdById: items[0].createdById || null,
        createdAt: serverTimestamp(),
        readBy: [],
        dismissedBy: [],
        expiresAt: items[0].expiresAt || null,
        actionable: false,
        actionUrl: this.getGroupActionUrl(items),
        actionLabel: 'View Details',
        groupKey,
        isGrouped: true,
        groupCount: items.length,
      };

      const docRef = await addDoc(collection(db, this.collectionName), groupData);

      // Mark individual items as grouped (if they have IDs)
      const batch = writeBatch(db);
      items.forEach((item) => {
        if (item.id) {
          batch.update(doc(db, this.collectionName, item.id), { isGrouped: true });
        }
      });
      await batch.commit();

      return docRef.id;
    } catch (error) {
      console.error('Error creating grouped notification:', error);
      throw error;
    }
  }

  /**
   * Get existing group header
   */
  async getGroupHeader(groupKey, schoolId) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('schoolId', '==', schoolId),
        where('groupKey', '==', groupKey),
        where('isGrouped', '==', true),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error('Error getting group header:', error);
      return null;
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId, userId) {
    if (!notificationId || !userId) {
      throw new Error('notificationId and userId are required');
    }

    try {
      await updateDoc(doc(db, this.collectionName, notificationId), {
        readBy: arrayUnion(userId),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds, userId) {
    if (!notificationIds || notificationIds.length === 0 || !userId) {
      return;
    }

    const batch = writeBatch(db);
    notificationIds.forEach((id) => {
      batch.update(doc(db, this.collectionName, id), {
        readBy: arrayUnion(userId),
      });
    });

    try {
      await batch.commit();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      throw error;
    }
  }

  /**
   * Dismiss notification
   */
  async dismiss(notificationId, userId) {
    if (!notificationId || !userId) {
      throw new Error('notificationId and userId are required');
    }

    try {
      await updateDoc(doc(db, this.collectionName, notificationId), {
        dismissedBy: arrayUnion(userId),
      });
    } catch (error) {
      console.error('Error dismissing notification:', error);
      throw error;
    }
  }

  /**
   * Get unread count for user
   */
  async getUnreadCount(userId, schoolId) {
    if (!userId || !schoolId) return 0;

    try {
      const q = query(
        collection(db, this.collectionName),
        where('schoolId', '==', schoolId),
        where('recipientIds', 'array-contains', userId)
      );

      const snapshot = await getDocs(q);
      const unreadCount = snapshot.docs.filter((doc) => {
        const data = doc.data();
        return !data.readBy?.includes(userId) && !data.dismissedBy?.includes(userId);
      }).length;

      return unreadCount;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  // === Helper Methods ===

  /**
   * Get icon name for notification type
   */
  getIconForType(type, priority) {
    if (priority === 'critical') return 'ExclamationCircleIcon';

    switch (type) {
      case 'mention':
        return 'ChatBubbleLeftIcon';
      case 'consequence':
        return 'ShieldExclamationIcon';
      case 'rule':
        return 'BoltIcon';
      case 'access':
        return 'UserPlusIcon';
      case 'system':
        return 'CogIcon';
      case 'parent_comm':
        return 'EnvelopeIcon';
      default:
        return 'BellIcon';
    }
  }

  /**
   * Generate group title
   */
  generateGroupTitle(items) {
    const count = items.length;
    const type = items[0].type;

    switch (type) {
      case 'mention':
        return `${count} Mentions Submitted`;
      case 'consequence':
        return `${count} Consequences Assigned`;
      case 'rule':
        return `${count} Rules Triggered`;
      case 'access':
        return `${count} Access Requests`;
      case 'parent_comm':
        return `${count} Parent Messages Sent`;
      default:
        return `${count} Notifications`;
    }
  }

  /**
   * Generate group message
   */
  generateGroupMessage(items) {
    return `Click to view ${items.length} individual notifications`;
  }

  /**
   * Calculate group priority (highest priority wins)
   */
  calculateGroupPriority(items) {
    const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    return items.reduce((highest, item) => {
      const itemPriority = priorityOrder[item.priority] || 1;
      const highestPriority = priorityOrder[highest] || 1;
      return itemPriority > highestPriority ? item.priority : highest;
    }, 'low');
  }

  /**
   * Merge metadata from multiple items
   */
  mergeGroupMetadata(existingMetadata, items) {
    const metadata = { ...existingMetadata };

    // Collect unique student IDs and names
    const studentIds = new Set(existingMetadata.affectedStudents || []);
    items.forEach((item) => {
      if (item.metadata?.studentId) {
        studentIds.add(item.metadata.studentId);
      }
    });
    metadata.affectedStudents = Array.from(studentIds);

    // Collect unique offence types
    const offenceTypes = new Set(existingMetadata.offenceTypes || []);
    items.forEach((item) => {
      if (item.metadata?.offenceName) {
        offenceTypes.add(item.metadata.offenceName);
      }
    });
    metadata.offenceTypes = Array.from(offenceTypes);

    return metadata;
  }

  /**
   * Get action URL for group
   */
  getGroupActionUrl(items) {
    const type = items[0].type;

    switch (type) {
      case 'mention':
      case 'consequence':
        return '/discipline/queue';
      case 'access':
        return '/admin/access-requests';
      case 'parent_comm':
        return '/communication/messages';
      default:
        return null;
    }
  }
}

export default new NotificationService();
